import { createContext, useContext } from 'react';

/**
 * The app banner is chrome, not content: `<AppBanner />` renders the user's
 * selected banner at the SAME spot on every screen, above <main>. Routes don't
 * render their own — they fill the two slots it exposes:
 *
 *   <BannerTopSlot>      the breathing row above the banner (the Hub puts its
 *                        game selector here; it is also the room the Hub's
 *                        featured avatar needs to poke above the banner)
 *   <BannerOverlaySlot>  a layer over the banner itself
 *
 * Both are portals, so a route declares its banner content inline in its own
 * JSX and React delivers it — no config object travelling through state, and
 * therefore none of the identity/loop hazards `useMainButton` has to dodge.
 */
export interface AppBannerContextValue {
  topNode: HTMLElement | null;
  setTopNode: (node: HTMLElement | null) => void;
  overlayNode: HTMLElement | null;
  setOverlayNode: (node: HTMLElement | null) => void;
  /**
   * La colonna a DESTRA del banner, che esiste solo quando il banner e'
   * compresso dallo scroll. Ospita il tasto della sezione attiva: sparita la
   * griglia, resta l'unica indicazione di dove ci si trova.
   *
   * E' un canale separato da `topNode` perche' i due contenuti coesistono: le
   * valute restano sulla riga in alto anche da compressi. Prima condividevano
   * lo stesso slot e si contendevano il posto.
   */
  sideNode: HTMLElement | null;
  setSideNode: (node: HTMLElement | null) => void;
  /**
   * La riga SOTTO il banner, ancora dentro la chrome.
   *
   * Serve a chi ha un'identità da stampare sotto lo stendardo — il Profilo ci
   * mette nickname e barra dell'esperienza. Sta nella chrome e non nella rotta
   * perché deve stare DENTRO la targa di cuoio che fa da fondo a tutto il
   * blocco: renderla dalla rotta l'avrebbe messa fuori, sopra le scaglie.
   */
  bottomNode: HTMLElement | null;
  setBottomNode: (node: HTMLElement | null) => void;
}

export const AppBannerContext = createContext<AppBannerContextValue | null>(null);

export function useAppBanner(): AppBannerContextValue {
  const ctx = useContext(AppBannerContext);
  if (!ctx) throw new Error('useAppBanner must be used inside <AppBannerProvider>');
  return ctx;
}
